// TypingChallenge Modal Component - TypeTown Bright Theme
// Main typing practice modal for level progression

import React, { useState, useEffect, useRef } from 'react';
import { Keyboard, Volume2 } from 'lucide-react';
import { useTTS } from '../../hooks/useTTS';

const TypingChallenge = ({ phrase, onComplete, onCancel, levelInfo, phrasesTypedInLevel = 0, phrasesPerLevel = 10, voiceEnabled = true, playerVoice = 'alice' }) => {
  const [typedText, setTypedText] = useState('');
  const [startTime, setStartTime] = useState(null);
  const [keystrokes, setKeystrokes] = useState(0);
  const [correctKeystrokes, setCorrectKeystrokes] = useState(0);
  const inputRef = useRef(null);
  const { speakAsPlayer, stop: stopTTS, isPlaying } = useTTS();
  
  // Calculate if this is the last phrase needed to complete the level
  const phrasesAfterThis = phrasesTypedInLevel + 1;
  const isLastPhrase = phrasesAfterThis >= phrasesPerLevel;
  
  // Reset state when phrase changes and read aloud
  useEffect(() => {
    setTypedText('');
    setStartTime(null);
    setKeystrokes(0);
    setCorrectKeystrokes(0);
    inputRef.current?.focus();
    
    if (voiceEnabled && phrase) {
      setTimeout(() => speakAsPlayer(phrase, playerVoice), 400);
    }
    return () => stopTTS();
  }, [phrase]);
  
  useEffect(() => {
    if (typedText.length === 1 && !startTime) setStartTime(Date.now());
  }, [typedText, startTime]);
  
  const readAloud = () => {
    if (phrase) speakAsPlayer(phrase, playerVoice);
  };
  
  const handleSubmit = () => {
    stopTTS();
    onComplete({ 
      phrase, 
      typed_text: typedText, 
      keystrokes, 
      correct_keystrokes: correctKeystrokes, 
      duration_seconds: startTime ? (Date.now() - startTime) / 1000 : 0 
    });
  };
  
  const accuracy = keystrokes > 0 ? Math.round((correctKeystrokes / keystrokes) * 100) : 100;
  
  return (
    <div className="fixed inset-0 bg-gradient-to-b from-sky-200/95 via-emerald-100/95 to-green-200/95 backdrop-blur-sm z-[500] flex items-center justify-center p-2 sm:p-4" data-testid="typing-challenge-modal">
      <div className="bg-white border-3 sm:border-4 border-sunny-yellow rounded-2xl sm:rounded-3xl p-4 sm:p-8 max-w-2xl w-full shadow-2xl shadow-sunny-yellow/20 max-h-[95vh] overflow-y-auto">
        {/* Header with TypeTown styling */}
        <div className="flex justify-between items-center mb-4 sm:mb-6">
          <div className="flex items-center gap-2 sm:gap-4">
            <div className="w-10 h-10 sm:w-16 sm:h-16 bg-gradient-to-br from-sunny-yellow to-coral-orange rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg">
              <Keyboard className="w-5 h-5 sm:w-8 sm:h-8 text-white" />
            </div>
            <div>
              <h2 className="text-xl sm:text-3xl font-display font-bold text-text-dark">Level {levelInfo.level}</h2>
              <p className="text-mint-green font-semibold text-sm sm:text-base">{levelInfo.stage_name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {voiceEnabled && (
              <button
                onClick={readAloud}
                disabled={isPlaying}
                className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center transition-all ${
                  isPlaying ? 'bg-mint-green/20 text-mint-green animate-pulse' : 'bg-lavender/20 text-lavender hover:bg-lavender/30'
                }`}
                data-testid="typing-challenge-listen-btn"
                title="Listen to phrase"
              >
                <Volume2 className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            )}
            <button 
              onClick={() => { stopTTS(); onCancel(); }} 
              className="w-8 h-8 sm:w-10 sm:h-10 bg-coral-orange/20 hover:bg-coral-orange/40 rounded-full flex items-center justify-center text-coral-orange text-lg sm:text-xl font-bold transition-all"
            >
              ✕
            </button>
          </div>
        </div>
        
        {/* Typing area with bright background - splits by WORDS for better readability */}
        <div className="bg-gradient-to-r from-cream to-warm-white rounded-xl sm:rounded-2xl p-3 sm:p-6 mb-4 sm:mb-6 border-2 border-sunny-yellow/30 shadow-inner max-h-[30vh] sm:max-h-[40vh] overflow-y-auto">
          <p className="text-base sm:text-xl md:text-2xl text-center font-mono tracking-wide leading-loose">
            {(() => {
              // Split phrase into words while preserving spaces
              const words = phrase.split(/(\s+)/);
              let charIndex = 0;
              
              return words.map((word, wordIdx) => {
                const wordStart = charIndex;
                const wordChars = word.split('').map((char, charIdx) => {
                  const globalIdx = wordStart + charIdx;
                  const isTyped = globalIdx < typedText.length;
                  const isCorrect = isTyped && typedText[globalIdx] === char;
                  const isCurrent = globalIdx === typedText.length;
                  
                  return (
                    <span 
                      key={`${wordIdx}-${charIdx}`}
                      className={
                        isTyped 
                          ? (isCorrect ? 'text-mint-green' : 'text-coral-orange bg-coral-orange/20 rounded') 
                          : isCurrent 
                            ? 'text-sunny-yellow bg-sunny-yellow/30 rounded animate-pulse font-bold' 
                            : 'text-text-medium/60'
                      }
                    >
                      {char === ' ' ? '\u00A0' : char}
                    </span>
                  );
                });
                charIndex += word.length;
                
                // Wrap each word in a span that doesn't break
                return (
                  <span key={wordIdx} className="inline-block whitespace-nowrap">
                    {wordChars}
                  </span>
                );
              });
            })()}
          </p>
        </div>
        
        {/* Input field with friendly styling */}
        <input 
          ref={inputRef} 
          type="text" 
          value={typedText}
          onChange={(e) => {
            const newValue = e.target.value;
            if (newValue.length > typedText.length) {
              setKeystrokes(prev => prev + 1);
              const lastChar = newValue[newValue.length - 1];
              const expectedChar = phrase[typedText.length];
              if (lastChar === expectedChar) {
                setCorrectKeystrokes(prev => prev + 1);
              }
            }
            if (newValue.length <= phrase.length) {
              setTypedText(newValue);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') { stopTTS(); onCancel(); return; }
          }}
          className="w-full px-3 sm:px-6 py-3 sm:py-4 bg-white border-2 sm:border-3 border-mint-green/50 rounded-xl sm:rounded-2xl text-text-dark text-center font-mono text-base sm:text-xl focus:outline-none focus:border-mint-green focus:ring-4 focus:ring-mint-green/20 mb-4 sm:mb-6 shadow-sm placeholder:text-text-medium/40"
          placeholder="Start typing here..."
          autoFocus
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
          data-testid="typing-challenge-input"
        />
        
        {/* Stats cards with bright colors */}
        <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-4 sm:mb-6">
          <div className="bg-gradient-to-br from-mint-green/20 to-mint-green/10 rounded-xl sm:rounded-2xl p-2 sm:p-4 text-center border-2 border-mint-green/30">
            <p className="text-text-medium text-xs sm:text-sm font-semibold">ACCURACY</p>
            <p className={`text-xl sm:text-3xl font-display font-bold ${accuracy >= 80 ? 'text-mint-green' : 'text-coral-orange'}`}>{accuracy}%</p>
          </div>
          <div className="bg-gradient-to-br from-sunny-yellow/20 to-sunny-yellow/10 rounded-xl sm:rounded-2xl p-2 sm:p-4 text-center border-2 border-sunny-yellow/30">
            <p className="text-text-medium text-xs sm:text-sm font-semibold">PROGRESS</p>
            <p className="text-xl sm:text-3xl font-display font-bold text-sunny-yellow">{typedText.length}/{phrase.length}</p>
          </div>
          <div className="bg-gradient-to-br from-lavender/20 to-lavender/10 rounded-xl sm:rounded-2xl p-2 sm:p-4 text-center border-2 border-lavender/30">
            <p className="text-text-medium text-xs sm:text-sm font-semibold">KEYSTROKES</p>
            <p className="text-xl sm:text-3xl font-display font-bold text-lavender">{keystrokes}</p>
          </div>
        </div>
        
        {/* Progress bar */}
        <div className="h-3 sm:h-4 bg-soft-gray rounded-full overflow-hidden shadow-inner">
          <div 
            className="h-full bg-gradient-to-r from-mint-green via-sunny-yellow to-coral-orange transition-all duration-200 rounded-full" 
            style={{ width: `${(typedText.length / phrase.length) * 100}%` }} 
          />
        </div>
        
        {/* Word progress for level */}
        <div className="mt-4 text-center">
          <p className="text-sm text-text-medium">
            Phrases this level: <span className="font-bold text-mint-green">{phrasesTypedInLevel}</span> / {phrasesPerLevel}
            {isLastPhrase && <span className="ml-2 text-sunny-yellow">Last one!</span>}
          </p>
        </div>
        
        {/* Complete button - shows "Next" or "Complete Level" */}
        {typedText.length >= phrase.length && (
          <button 
            onClick={handleSubmit} 
            className={`w-full mt-4 text-white font-display font-bold py-4 rounded-2xl text-xl transition-all hover:scale-[1.02] shadow-lg ${
              isLastPhrase 
                ? 'bg-gradient-to-r from-sunny-yellow to-coral-orange hover:from-coral-orange hover:to-sunny-yellow shadow-sunny-yellow/30' 
                : 'bg-gradient-to-r from-mint-green to-sky-blue hover:from-sky-blue hover:to-mint-green shadow-mint-green/30'
            }`}
            data-testid="typing-challenge-submit"
          >
            {isLastPhrase ? 'Complete Level!' : 'Next'}
          </button>
        )}
      </div>
    </div>
  );
};

export default TypingChallenge;
